import { FunctionComponent, useState } from "react";
import { EstimateResultsResponseDto } from "@type/estimateResults/estimateResults.response";
import ResultSynthesis from "@components/result/ResultSynthesis";
import ResultDetails from "@components/result/ResultDetails";
import BasicDivider from "@components/divider/BasicDivider";
import AnswerCard from "@components/cards/AnswerCard";
import ExpertCard from "@components/cards/ExpertCard";
import TipsModal from "@components/modals/TipsModal";
import FaqModal from "@components/modals/FaqModal";
import { useTranslation } from "react-i18next";

interface ResultsProps {
  title: string | JSX.Element;
  type: "farmer" | "collectivity";
  results?: EstimateResultsResponseDto;
  className?: string;
  titleClassName?: string;
  onShare?: () => void;
  onSave?: () => void;
}

const Results: FunctionComponent<ResultsProps> = ({
  title,
  type,
  results,
  titleClassName = "",
  onShare,
  onSave,
}) => {
  const { t } = useTranslation();
  const [isTipsModalVisible, setIsTipsModalVisible] = useState<boolean>(false);
  const [isFaqModalVisible, setIsFaqModalVisible] = useState<boolean>(false);
  const [faqModalCategory, setFaqModalCategory] = useState<
    string | undefined
  >();

  const openFaq = (category: string) => {
    setIsFaqModalVisible(true);
    setFaqModalCategory(category);
  };

  return results ? (
    <>
      <TipsModal
        visible={isTipsModalVisible}
        onCancel={() => setIsTipsModalVisible(false)}
        title="Comment optimiser votre capacité de production ?"
        onClickExpert={() =>
          window.open("https://projet-methanisation.grdf.fr/contact", "_blank")
        }
        onShare={() => {
          onShare && onShare();
          setIsTipsModalVisible(false);
          setIsFaqModalVisible(false);
        }}
        onSave={() => {
          onSave && onSave();
          setIsTipsModalVisible(false);
          setIsFaqModalVisible(false);
        }}
      />
      <FaqModal
        visible={isFaqModalVisible}
        onCancel={() => setIsFaqModalVisible(false)}
        category={faqModalCategory}
        type={type}
      />
      <div className="px-4 px-lg-64">
        <h3 className={`flex-grow-1 mb-4 text-black ${titleClassName}`}>
          {title}
        </h3>
        <div className="mb-4 text-sm">
          <span className="text-single-line">
            {t(`results.disclaimer-${type}`)}
          </span>
          <div>
            <a
              className="cursor-pointer"
              onClick={() => {
                setFaqModalCategory(`calcul-${type}`);
                setIsFaqModalVisible(true);
              }}
              tabIndex={0}
              onKeyPress={(key) => {
                if (key.charCode === 13) {
                  setFaqModalCategory(`calcul-${type}`);
                  setIsFaqModalVisible(true);
                }
              }}
            >
              En savoir plus
            </a>
          </div>
        </div>
      </div>
      <div className="px-0 px-sm-4 px-lg-64 pb-32">
        <ResultSynthesis
          synthesis={results.synthesis}
          type={type}
          onOpenFaq={openFaq}
          onOpenTips={() => {
            setIsTipsModalVisible(true);
          }}
        />
      </div>
      <div>
        <BasicDivider
          variant="white"
          height={24}
          className="w-100 border-radius-0 mt-0 mb-0"
        />
      </div>
      <div className="px-0 px-sm-4 px-lg-64 py-32">
        <ResultDetails
          synthesis={results.synthesis}
          resultDetails={results.details}
          resultImpacts={results.impacts}
          type={type}
          onOpenFaq={openFaq}
          onOpenTips={() => {
            setIsTipsModalVisible(true);
          }}
        />
      </div>
      <div>
        <BasicDivider
          variant="white"
          height={24}
          className="w-100 border-radius-0 mt-0 mb-0"
        />
      </div>
      <div className="px-4 px-lg-64 py-32">
        <AnswerCard />
      </div>
      <div>
        <BasicDivider
          variant="white"
          height={24}
          className="w-100 border-radius-0 mt-0 mb-0"
        />
      </div>
      <div>
        <ExpertCard
          className="border-radius-0"
          onClick={() => {
            window.open(
              "https://projet-methanisation.grdf.fr/contact",
              "_blank"
            );
          }}
        />
      </div>
    </>
  ) : (
    <div className="px-0 px-sm-4 px-lg-64">
      <ResultSynthesis
        type={type}
        onOpenFaq={openFaq}
        onOpenTips={() => {
          setIsTipsModalVisible(true);
        }}
      />
    </div>
  );
};

export default Results;
